import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import {
  fetchFoodAdminOperationRuns,
  fetchFoodOsmStoreNormalizationSummary,
  fetchFoodSupermarketRefreshStatus,
  postFoodAdminAccessRecalculationDryRun,
  postFoodAdminAccessRecalculationRun,
  postFoodAdminOsmIngestDryRun,
  postFoodAdminOsmIngestRun,
} from "../../../../api/foodApi";
import { ADMIN_PERMISSIONS } from "../../../../auth/adminPermissions";
import { useAuth } from "../../../../auth/useAuth";
import { FOOD_OSM_SOURCE_KEYS } from "../../../../constants/foodSourceConstants";
import {
  FOOD_ACCESS_ABURRA_REGION,
  FOOD_ACCESS_MUNICIPALITY_OPTIONS,
} from "../../../../constants/foodAccessGeography";
import { formatRelativeFreshness } from "../../../../utils/foodFormatters";
import FoodManualRefreshPlaybookSection from "./FoodManualRefreshPlaybookSection";
import FoodOsmRefreshSchedulerSection from "./FoodOsmRefreshSchedulerSection";
import FoodWalkingAccessSection from "./FoodWalkingAccessSection";
import FoodFreshFoodSection from "./FoodFreshFoodSection";

const DEFAULT_CITY = "Medellín";
const DEFAULT_DEPARTMENT = "Antioquia";
const DEFAULT_AREA_TYPE = "NEIGHBORHOOD";
const REGION_SCOPE_MEDELLIN = "MEDELLIN";
const REGION_SCOPE_ABURRA = "ABURRA_VALLEY";

const OSM_CONFIRM_MESSAGE =
  "You are about to run a live OSM supermarket ingest for OSM_FOOD_POIS. This may insert or update supermarket store records. Continue?";

function buildAccessConfirmMessage({ regionScope, municipality, areaType, city, department }) {
  if (regionScope === REGION_SCOPE_ABURRA && !municipality) {
    return `You are about to recalculate Food Access Areas for Aburrá Valley (${department}) / ${areaType} using the current trusted store dataset. Continue?`;
  }
  if (municipality) {
    return `You are about to recalculate Food Access Areas for ${municipality} (${department}) / ${areaType}. Continue?`;
  }
  return `You are about to recalculate Food Access Areas for ${city} / ${department} / ${areaType} using the current trusted store dataset. Continue?`;
}

function buildAccessPayload({ regionScope, municipality, city, department, areaType }) {
  if (regionScope === REGION_SCOPE_ABURRA && !municipality) {
    return {
      region: FOOD_ACCESS_ABURRA_REGION,
      department,
      areaType,
    };
  }
  if (municipality) {
    return {
      municipality,
      department,
      areaType,
    };
  }
  return {
    city,
    department,
    areaType,
  };
}

function parseApiError(error) {
  if (error?.body?.error === "RUN_ALREADY_IN_PROGRESS") {
    return error.body.message || "A run is already in progress.";
  }
  if (error?.body?.error === "REFRESH_WORKFLOW_IN_PROGRESS") {
    return "A supermarket refresh workflow is currently running.";
  }
  return error?.body?.message || error?.message || "Operation failed.";
}

const FoodAccessOperationsPage = ({ onClose }) => {
  const { hasPermission, isSuperAdmin } = useAuth();
  const canManage =
    isSuperAdmin || hasPermission(ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_MANAGE);

  const [city, setCity] = useState(DEFAULT_CITY);
  const [department, setDepartment] = useState(DEFAULT_DEPARTMENT);
  const [areaType, setAreaType] = useState(DEFAULT_AREA_TYPE);
  const [regionScope, setRegionScope] = useState(REGION_SCOPE_MEDELLIN);
  const [municipality, setMunicipality] = useState("");
  const [status, setStatus] = useState(null);
  const [normalizationSummary, setNormalizationSummary] = useState(null);
  const [operationRuns, setOperationRuns] = useState([]);
  const [expandedRunId, setExpandedRunId] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [osmRunning, setOsmRunning] = useState(false);
  const [accessRunning, setAccessRunning] = useState(false);
  const [osmResult, setOsmResult] = useState(null);
  const [accessResult, setAccessResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const operationBusy = osmRunning || accessRunning;

  const latestOsmRun = useMemo(
    () =>
      operationRuns.find((run) => run.operationType === "OSM_FOOD_POIS_INGEST") || null,
    [operationRuns]
  );

  const accessPayload = useMemo(
    () => buildAccessPayload({ regionScope, municipality, city, department, areaType }),
    [regionScope, municipality, city, department, areaType]
  );

  const load = useCallback(async () => {
    try {
      setLoadingStatus(true);
      const [refreshStatus, normSummary, runs] = await Promise.all([
        fetchFoodSupermarketRefreshStatus(accessPayload),
        fetchFoodOsmStoreNormalizationSummary({ sourceKey: FOOD_OSM_SOURCE_KEYS.FOOD_POIS }),
        fetchFoodAdminOperationRuns({ limit: 15 }),
      ]);
      setStatus(refreshStatus);
      setNormalizationSummary(normSummary);
      setOperationRuns(runs.runs || []);
      setErrorMessage("");
    } catch (error) {
      setErrorMessage(parseApiError(error));
    } finally {
      setLoadingStatus(false);
    }
  }, [accessPayload]);

  useEffect(() => {
    load();
  }, [load]);

  const handleOsmDryRun = async () => {
    if (!canManage || operationBusy) return;
    try {
      setOsmRunning(true);
      setErrorMessage("");
      const result = await postFoodAdminOsmIngestDryRun({ city, department });
      setOsmResult(result);
      await load();
    } catch (error) {
      setErrorMessage(parseApiError(error));
    } finally {
      setOsmRunning(false);
    }
  };

  const handleOsmRealRun = async () => {
    if (!canManage || operationBusy) return;
    if (!window.confirm(OSM_CONFIRM_MESSAGE)) return;
    try {
      setOsmRunning(true);
      setErrorMessage("");
      const result = await postFoodAdminOsmIngestRun({
        city,
        department,
        confirm: true,
      });
      setOsmResult(result);
      await load();
    } catch (error) {
      setErrorMessage(parseApiError(error));
    } finally {
      setOsmRunning(false);
    }
  };

  const handleAccessDryRun = async () => {
    if (!canManage || operationBusy) return;
    try {
      setAccessRunning(true);
      setErrorMessage("");
      const result = await postFoodAdminAccessRecalculationDryRun(accessPayload);
      setAccessResult(result);
      await load();
    } catch (error) {
      setErrorMessage(parseApiError(error));
    } finally {
      setAccessRunning(false);
    }
  };

  const handleAccessRealRun = async () => {
    if (!canManage || operationBusy) return;
    if (
      !window.confirm(
        buildAccessConfirmMessage({ regionScope, municipality, areaType, city, department })
      )
    )
      return;
    try {
      setAccessRunning(true);
      setErrorMessage("");
      const result = await postFoodAdminAccessRecalculationRun({
        ...accessPayload,
        confirm: true,
      });
      setAccessResult(result);
      await load();
    } catch (error) {
      setErrorMessage(parseApiError(error));
    } finally {
      setAccessRunning(false);
    }
  };

  return (
    <div className="admin-page food-access-operations-page">
      <header className="admin-page__header">
        <div>
          <p className="admin-page__eyebrow">Food Data</p>
          <h1>Food Access Operations</h1>
          <p className="muted">
            Safe one-click OSM supermarket ingest and neighborhood access recalculation for the
            Food Access Map.
          </p>
        </div>
        {onClose ? (
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Close
          </button>
        ) : null}
      </header>

      <section className="panel-card food-access-operations-page__guardrails" role="status">
        <h2>Safety guardrails</h2>
        <ul className="food-wholesale-intelligence__list">
          <li>Run a dry run first — real runs require confirmation.</li>
          <li>OSM data may contain duplicates, stale branches, or incomplete tags.</li>
          <li>
            Access scores use straight-line centroid distances, not walking-network isochrones.
          </li>
          <li>Resolve review queue items for best data quality before trusting map coverage.</li>
          <li>Recalculate access areas after meaningful store dataset updates.</li>
          <li>Mock OSM ingest is not exposed from this Admin workflow.</li>
        </ul>
      </section>

      {!canManage ? (
        <p className="food-dashboard__error" role="alert">
          You need source management permission to run Admin operations.
        </p>
      ) : null}

      {errorMessage ? (
        <p className="food-dashboard__error" role="alert">
          {errorMessage}
        </p>
      ) : null}

      <FoodManualRefreshPlaybookSection
        canManage={canManage}
        geography={accessPayload}
      />

      <FoodOsmRefreshSchedulerSection canManage={canManage} />

      <FoodWalkingAccessSection canManage={canManage} />
      <FoodFreshFoodSection canManage={canManage} />

      <section className="panel-card">
        <h2>Chain locator audits</h2>
        <p className="muted">
          Track quarterly branch file freshness, source provenance, and safe re-import guidance for supported
          supermarket chains.
        </p>
        <a className="btn btn--secondary" href="#/admin/food-chain-locator-audits">
          Open Chain Locator Audits
        </a>
      </section>

      <section className="panel-card">
        <h2>Current status</h2>
        {loadingStatus ? (
          <p className="muted">Loading operational status…</p>
        ) : status ? (
          <>
            <div className="food-comparison-summary-cards">
              <div className="food-comparison-summary-cards__card">
                <span className="muted">OSM ingest</span>
                <strong>{formatRelativeFreshness(status.osm?.latestIngestAt)}</strong>
                <span>{status.osm?.latestStatus || "NO_DATA"}</span>
              </div>
              <div className="food-comparison-summary-cards__card">
                <span className="muted">Trusted stores</span>
                <strong>{status.stores?.trustedStoreCount ?? "—"}</strong>
                <span>{status.osm?.rowCount ?? 0} OSM rows</span>
              </div>
              <div className="food-comparison-summary-cards__card">
                <span className="muted">Review queue</span>
                <strong>{status.review?.reviewQueueCount ?? "—"}</strong>
                <span>{status.review?.unresolvedConflictCount ?? 0} conflicts</span>
              </div>
              <div className="food-comparison-summary-cards__card">
                <span className="muted">Access recalculation</span>
                <strong>{formatRelativeFreshness(status.access?.latestCalculatedAt)}</strong>
                <span>{status.access?.areaCount ?? 0} areas scored</span>
              </div>
            </div>
            <p>
              Health: <strong>{status.health?.status}</strong> — {status.health?.nextRecommendedAction}
            </p>
            <p className="muted">
              Low/no-access neighborhoods: {status.access?.lowOrNoAccessCount ?? 0} · Low-confidence
              geocodes: {status.review?.lowConfidenceGeocodeCount ?? 0}
            </p>
          </>
        ) : (
          <p className="muted">Unable to load status.</p>
        )}
      </section>

      <section className="panel-card">
        <h2>OSM Supermarket Ingestion</h2>
        <p className="muted">
          Source: <code>{FOOD_OSM_SOURCE_KEYS.FOOD_POIS}</code> · Live Overpass ingest (no mock mode)
        </p>
        {latestOsmRun?.summary?.bbox ? (
          <p className="muted">
            Bbox: south {latestOsmRun.summary.bbox.south}, west {latestOsmRun.summary.bbox.west},
            north {latestOsmRun.summary.bbox.north}, east {latestOsmRun.summary.bbox.east}
            {latestOsmRun.summary.bboxOverride
              ? ` · override ${latestOsmRun.summary.bboxOverride}`
              : null}
          </p>
        ) : null}
        {latestOsmRun ? (
          <ul>
            <li>
              Latest run: {latestOsmRun.dryRun ? "dry run" : "real run"} · {latestOsmRun.status}
            </li>
            <li>
              Fetched/parsed: {latestOsmRun.summary?.fetchedCount ?? "—"} /{" "}
              {latestOsmRun.summary?.parsedCount ?? "—"}
            </li>
            <li>
              Inserted/updated/skipped: {latestOsmRun.summary?.insertedCount ?? "—"} /{" "}
              {latestOsmRun.summary?.updatedCount ?? "—"} /{" "}
              {latestOsmRun.summary?.skippedCount ?? "—"}
            </li>
          </ul>
        ) : (
          <p className="muted">No Admin OSM ingest runs logged yet.</p>
        )}
        <div className="food-normalization-page__control-row">
          <button
            type="button"
            className="btn btn--ghost"
            disabled={!canManage || operationBusy}
            onClick={handleOsmDryRun}
          >
            {osmRunning ? "Running OSM dry run…" : "Run OSM Dry Run"}
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={!canManage || operationBusy}
            onClick={handleOsmRealRun}
          >
            Run OSM Real Ingest
          </button>
        </div>
        {osmResult ? (
          <pre className="food-normalization-page__summary">{JSON.stringify(osmResult, null, 2)}</pre>
        ) : null}
      </section>

      <section className="panel-card">
        <h2>OSM Normalization / Review Status</h2>
        {normalizationSummary ? (
          <ul>
            <li>Auto-normalized trusted: {normalizationSummary.autoNormalized ?? 0}</li>
            <li>Needs review: {normalizationSummary.needsReview ?? 0}</li>
            <li>Marked duplicates: {normalizationSummary.markedDuplicates ?? 0}</li>
            <li>
              Last normalized: {formatRelativeFreshness(normalizationSummary.lastNormalizedAt)}
            </li>
          </ul>
        ) : (
          <p className="muted">Normalization summary unavailable.</p>
        )}
        <p>
          <a href="#/admin/food-store-review-queue">Open store review queue</a>
        </p>
      </section>

      <section className="panel-card">
        <h2>Food Access Area Recalculation</h2>
        <div className="food-normalization-page__control-row">
          <label>
            Region scope{" "}
            <select
              value={regionScope}
              onChange={(event) => {
                const nextScope = event.target.value;
                setRegionScope(nextScope);
                if (nextScope === REGION_SCOPE_ABURRA) {
                  setMunicipality("");
                  setAreaType("MUNICIPALITY");
                } else {
                  setMunicipality("");
                  setCity(DEFAULT_CITY);
                  setAreaType(DEFAULT_AREA_TYPE);
                }
              }}
            >
              <option value={REGION_SCOPE_MEDELLIN}>Medellín (neighborhoods)</option>
              <option value={REGION_SCOPE_ABURRA}>Aburrá Valley</option>
            </select>
          </label>
          <label>
            Municipality{" "}
            <select
              value={municipality}
              onChange={(event) => {
                const nextMunicipality = event.target.value;
                setMunicipality(nextMunicipality);
                if (nextMunicipality) {
                  setCity(nextMunicipality);
                  setAreaType(
                    nextMunicipality === DEFAULT_CITY ? DEFAULT_AREA_TYPE : "MUNICIPALITY"
                  );
                } else if (regionScope === REGION_SCOPE_ABURRA) {
                  setAreaType("MUNICIPALITY");
                }
              }}
            >
              <option value="">
                {regionScope === REGION_SCOPE_ABURRA ? "All Aburrá Valley" : "Medellín default"}
              </option>
              {FOOD_ACCESS_MUNICIPALITY_OPTIONS.filter((option) => option.value).map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Department{" "}
            <input value={department} onChange={(event) => setDepartment(event.target.value)} />
          </label>
          <label>
            Area type{" "}
            <select value={areaType} onChange={(event) => setAreaType(event.target.value)}>
              <option value="NEIGHBORHOOD">NEIGHBORHOOD</option>
              <option value="MUNICIPALITY">MUNICIPALITY</option>
            </select>
          </label>
        </div>
        <p className="muted">
          Medellín neighborhood scoring uses barrio polygons. Other municipalities use
          municipality-level centroids until neighborhood boundaries are imported.
        </p>
        <div className="food-normalization-page__control-row">
          <button
            type="button"
            className="btn btn--ghost"
            disabled={!canManage || operationBusy}
            onClick={handleAccessDryRun}
          >
            {accessRunning ? "Running access dry run…" : "Run Recalculation Dry Run"}
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={!canManage || operationBusy}
            onClick={handleAccessRealRun}
          >
            Run Recalculation Real Run
          </button>
        </div>
        {accessResult ? (
          <pre className="food-normalization-page__summary">
            {JSON.stringify(accessResult, null, 2)}
          </pre>
        ) : null}
      </section>

      <section className="panel-card">
        <h2>Latest results / run history</h2>
        {operationRuns.length ? (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Operation</th>
                <th>Dry run</th>
                <th>Status</th>
                <th>Started</th>
                <th>Duration</th>
                <th>Summary</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {operationRuns.map((run) => (
                <Fragment key={run.id}>
                  <tr>
                    <td>{run.operationType}</td>
                    <td>{run.dryRun ? "Yes" : "No"}</td>
                    <td>{run.status}</td>
                    <td>{formatRelativeFreshness(run.startedAt)}</td>
                    <td>{run.durationMs != null ? `${run.durationMs} ms` : "—"}</td>
                    <td>
                      {run.operationType === "OSM_FOOD_POIS_INGEST"
                        ? `+${run.summary?.insertedCount ?? 0} / ~${run.summary?.updatedCount ?? 0}`
                        : `${run.summary?.updatedAreaCount ?? 0} areas · ${run.summary?.trustedStoreCount ?? 0} stores`}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() =>
                          setExpandedRunId((current) => (current === run.id ? null : run.id))
                        }
                      >
                        {expandedRunId === run.id ? "Hide" : "Details"}
                      </button>
                    </td>
                  </tr>
                  {expandedRunId === run.id ? (
                    <tr key={`${run.id}-details`}>
                      <td colSpan={7}>
                        <pre className="food-normalization-page__summary">
                          {JSON.stringify(run, null, 2)}
                        </pre>
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="muted">No Admin operation runs logged yet.</p>
        )}
      </section>

      <section className="panel-card">
        <h2>Related Admin tools</h2>
        <div className="food-normalization-page__control-row">
          <a href="/food/food-access" className="btn btn--ghost">
            Food Access Map
          </a>
          <a href="#/admin/food-store-review-queue" className="btn btn--ghost">
            Store Review Queue
          </a>
          <a href="#/admin/food-store-reconciliation" className="btn btn--ghost">
            Store Reconciliation
          </a>
          <a href="#/admin/food-access-map" className="btn btn--ghost">
            Access Calculation Runs
          </a>
          <a href="#/admin/real-estate-source-controls" className="btn btn--ghost">
            Source Controls
          </a>
        </div>
      </section>
    </div>
  );
};

export default FoodAccessOperationsPage;
